import { Link, useParams } from "react-router-dom";
import { articles, articleImages, articleSidebarImage } from "@/mocks/article";
import type { ArticleData } from "@/types/article";
import Footer from "@/pages/home/components/Footer";
import { ArticleJsonLd } from "@/components/base/JsonLd";

export interface ArticleContentBlock {
  type: "heading" | "paragraph";
  text: string;
}

export interface ArticleMeta {
  title: string;
  slug: string;
  date: string;
  category: string;
  excerpt: string;
  readTime: string;
  imageSeq: string;
  imageSeqSecondary: string;
}

export type { ArticleData } from "@/types/article";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articles[slug] : undefined;

  if (!article) {
    return (
      <>
        <div className="min-h-screen bg-background-50 flex items-center justify-center">
          <div className="text-center px-6">
            <div className="w-20 h-20 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-6">
              <i className="ri-article-line text-3xl text-foreground-400 w-8 h-8 flex items-center justify-center"></i>
            </div>
            <h1 className="text-[28px] font-bold text-foreground-950 font-heading mb-3">Article Not Found</h1>
            <p className="text-[15px] text-foreground-500 mb-8">The article you are looking for does not exist or has been moved.</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 h-11 px-6 bg-foreground-900 text-background-50 rounded-full text-[14px] font-medium hover:bg-foreground-800 transition-colors duration-200 whitespace-nowrap"
            >
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
              Back to Home
            </Link>
          </div>
        </div>
      </>
    );
  }

  const { meta, content } = article;
  const articleUrl = `https://cssspreadsheet.living/article/${meta.slug}`;
  const articleImage = articleImages[meta.imageSeq] || "";

  return (
    <>
      <ArticleJsonLd
        article={article}
        url={articleUrl}
        publisherName="CSSSpreadsheet.living"
        publisherUrl="https://cssspreadsheet.living"
        imageUrl={articleImage}
      />
      <div className="min-h-screen bg-background-50">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-8">
          <nav className="text-[13px] text-foreground-500 mb-10 flex flex-wrap items-center gap-x-2 gap-y-1">
            <Link to="/" className="hover:text-foreground-800 transition-colors duration-200 whitespace-nowrap">
              Home
            </Link>
            <span className="text-foreground-300">/</span>
            <span className="text-foreground-600 whitespace-nowrap">{meta.category}</span>
            <span className="text-foreground-300">/</span>
            <span className="text-foreground-800 font-medium truncate">{meta.title.slice(0, 55)}&hellip;</span>
          </nav>
        </div>

        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
            <article className="lg:w-[70%]">
              <span className="inline-block px-3 py-1.5 bg-secondary-100 text-foreground-700 text-[11px] font-medium tracking-[0.04em] uppercase rounded-md mb-5">
                {meta.category}
              </span>
              <h1 className="text-[34px] md:text-[42px] font-bold text-foreground-950 font-heading leading-[1.12] tracking-[-0.01em]">
                {meta.title}
              </h1>

              <div className="mt-5 flex flex-wrap items-center gap-3 text-[13px] text-foreground-500">
                <span>{meta.date}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span>{meta.readTime}</span>
              </div>

              <div className="mt-8 w-full h-[320px] rounded-xl overflow-hidden bg-background-200">
                <img
                  src={articleImage}
                  alt={meta.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div className="mt-10 prose-custom">
                {content.map((block, idx) => {
                  if (block.type === "heading") {
                    return (
                      <h2
                        key={idx}
                        className="text-[24px] md:text-[28px] font-bold text-foreground-950 font-heading mt-12 mb-5 leading-[1.25]"
                      >
                        {block.text}
                      </h2>
                    );
                  }
                  if (block.type === "paragraph") {
                    const textWithLinks = block.text.replace(
                      /CSSBuy spreadsheet/gi,
                      (match) =>
                        `<a href="/" class="text-accent-700 underline decoration-accent-300 hover:decoration-accent-500 transition-colors">${match}</a>`
                    );
                    return (
                      <p
                        key={idx}
                        className="text-[16px] text-foreground-700 leading-[1.8] mb-5"
                        dangerouslySetInnerHTML={{ __html: textWithLinks }}
                      />
                    );
                  }
                  return null;
                })}
              </div>

              {meta.imageSeqSecondary && (
                <div className="mt-14 w-full h-[280px] rounded-xl overflow-hidden bg-background-200">
                  <img
                    src={articleImages[meta.imageSeqSecondary]}
                    alt="Article illustration"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              )}

              <div className="mt-12 p-8 md:p-10 bg-accent-100/60 rounded-2xl text-center">
                <p className="text-[20px] md:text-[22px] font-semibold text-foreground-900 font-heading">
                  Ready to find your next deal?
                </p>
                <p className="mt-2 text-[15px] text-foreground-600">
                  Browse the full CSSBuy spreadsheet collection and start building your haul today.
                </p>
                <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 h-11 px-6 bg-foreground-900 text-background-50 rounded-full text-[14px] font-medium hover:bg-foreground-800 transition-colors duration-200 whitespace-nowrap"
                  >
                    <i className="ri-home-4-line w-4 h-4 flex items-center justify-center"></i>
                    Return to Homepage
                  </Link>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 h-11 px-6 bg-background-50 border border-secondary-300 text-foreground-700 rounded-full text-[14px] font-medium hover:border-foreground-400 transition-colors duration-200 whitespace-nowrap"
                  >
                    Visit Main Site
                    <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
                  </a>
                </div>
              </div>

              <div className="mt-10 flex flex-wrap gap-4 pt-8 border-t border-background-200">
                <span className="text-[14px] font-medium text-foreground-800 whitespace-nowrap">More Articles:</span>
                {Object.values(articles)
                  .filter((a) => a.meta.slug !== meta.slug)
                  .slice(0, 3)
                  .map((a) => (
                    <Link
                      key={a.meta.slug}
                      to={`/article/${a.meta.slug}`}
                      className="text-[13px] text-accent-600 hover:text-accent-700 transition-colors duration-200 whitespace-nowrap"
                    >
                      {a.meta.title.slice(0, 50)}&hellip;
                    </Link>
                  ))}
              </div>
            </article>

            <aside className="lg:w-[30%]">
              <div className="sticky top-24">
                <div className="w-full rounded-2xl overflow-hidden bg-background-200" style={{ aspectRatio: "3/4" }}>
                  <img
                    src={articleSidebarImage[meta.slug] || articleSidebarImage["cssbuy-spreadsheet-ultimate-guide"]}
                    alt="Product Collection"
                    className="w-full h-full object-cover object-top"
                  />
                </div>

                <div className="mt-6 p-6 bg-background-100 rounded-2xl">
                  <h3 className="text-[16px] font-semibold text-foreground-900 mb-4">Explore Categories</h3>
                  <ul className="space-y-2">
                    <li>
                      <Link
                        to="/"
                        className="text-[13px] text-accent-600 hover:text-accent-700 transition-colors duration-200 flex items-center gap-2"
                      >
                        <i className="ri-arrow-left-s-line w-4 h-4 flex items-center justify-center"></i>
                        Back to Home
                      </Link>
                    </li>
                    <li>
                      <a
                        href="/#categories"
                        className="text-[13px] text-foreground-600 hover:text-foreground-900 transition-colors duration-200 flex items-center gap-2"
                      >
                        <i className="ri-grid-line w-4 h-4 flex items-center justify-center"></i>
                        All Categories
                      </a>
                    </li>
                    <li>
                      <a
                        href="/#guides"
                        className="text-[13px] text-foreground-600 hover:text-foreground-900 transition-colors duration-200 flex items-center gap-2"
                      >
                        <i className="ri-book-read-line w-4 h-4 flex items-center justify-center"></i>
                        Buyer&apos;s Guides
                      </a>
                    </li>
                  </ul>
                </div>

                <div className="mt-4 p-6 bg-background-100 rounded-2xl">
                  <h3 className="text-[16px] font-semibold text-foreground-900 mb-4">More Articles</h3>
                  <ul className="space-y-3">
                    {Object.values(articles)
                      .filter((a) => a.meta.slug !== meta.slug)
                      .map((a) => (
                        <li key={a.meta.slug}>
                          <Link
                            to={`/article/${a.meta.slug}`}
                            className="group block"
                          >
                            <span className="text-[13px] font-medium text-foreground-800 group-hover:text-accent-600 transition-colors duration-200 line-clamp-2">
                              {a.meta.title}
                            </span>
                            <span className="text-[11px] text-foreground-500 mt-0.5 block">
                              {a.meta.category} &middot; {a.meta.readTime}
                            </span>
                          </Link>
                        </li>
                      ))}
                  </ul>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}